import PQueue from 'p-queue';
import Api from './axios';
import { AxiosRequestConfig } from 'axios';

interface QueueStatus {
  size: number;
  pending: number;
}

interface AllQueueStatus {
  read: QueueStatus;
  write: QueueStatus;
  search: QueueStatus;
}

/**
 * QUEUE CONFIGURATION
 * 
 * We create separate queues for different types of operations to optimize performance:
 * - readQueue: For GET requests (can handle more concurrent requests)
 * - writeQueue: For POST/PUT/DELETE requests (more restrictive to prevent data conflicts)
 * - searchQueue: For search operations (most restrictive to prevent server overload)
 * 
 * IMPORTANT: No requests are dropped - all requests are queued and processed
 */
const readQueue = new PQueue({ 
  concurrency: 10,  // Allow 10 GET requests to run simultaneously
  timeout: 0,      // No timeout - never drop requests
  autoStart: true  // Start processing immediately
});

const writeQueue = new PQueue({ 
  concurrency: 2,  // Allow only 2 write operations simultaneously (POST/PUT/DELETE/PATCH)
  timeout: 0,      // No timeout - never drop requests
  autoStart: true  // Start processing immediately
});

const searchQueue = new PQueue({ 
  concurrency: 3,  // Allow 3 search operations at a time
  timeout: 0,      // No timeout - never drop requests
  autoStart: true  // Start processing immediately
});

/**
 * QUEUE MONITORING
 * 
 * Monitor queue sizes to detect potential issues
 */
const logQueueStatus = (): AllQueueStatus => {
  const status: AllQueueStatus = {
    read: { size: readQueue.size, pending: readQueue.pending },
    write: { size: writeQueue.size, pending: writeQueue.pending },
    search: { size: searchQueue.size, pending: searchQueue.pending }
  };
  
  // Log warning if any queue has more than 10 pending requests
  Object.entries(status).forEach(([queueName, queueStatus]) => {
    if (queueStatus.size > 10) {
      console.warn(`⚠️ ${queueName}Queue has ${queueStatus.size} requests waiting`);
    }
  });
  
  return status;
};

/**
 * ThrottledApi Class
 * 
 * This class wraps all API calls with automatic queuing (no dropping).
 * It provides a clean interface that automatically handles:
 * 1. Request queuing (prevents overwhelming the server)
 * 2. Concurrent request limits (different limits for different operations)
 * 3. Queue monitoring (warns when queues get large)
 * 
 * IMPORTANT: No requests are dropped - all requests are processed
 */
class ThrottledApi {
  /**
   * GET requests - queued (no throttling)
   * 
   * @param {string} url - The API endpoint URL
   * @param {Object} config - Axios configuration object
   * @returns {Promise} - Queued GET request
   * 
   * How it works:
   * 1. Request is added to readQueue (max 3 concurrent)
   * 2. All requests are processed in order (no dropping)
   * 3. Queue monitoring warns if queue gets too large
   */
  static async get(url: string, config?: AxiosRequestConfig) {
    // Log queue status before adding request
    logQueueStatus();
    
    return readQueue.add(() => Api.get(url, config));
  }

  /**
   * POST requests - queued (no throttling)
   * 
   * @param {string} url - The API endpoint URL
   * @param {Object} data - Data to send in the request body
   * @param {Object} config - Axios configuration object
   * @returns {Promise} - Queued POST request
   * 
   * How it works:
   * 1. Request is added to writeQueue (max 2 concurrent)
   * 2. All requests are processed in order (no dropping)
   * 3. More restrictive than GET to prevent data conflicts
   */
  static async post(url: string, data?: any, config?: AxiosRequestConfig) {
    // Log queue status before adding request
    logQueueStatus();
    
    return writeQueue.add(() => Api.post(url, data, config));
  }

  /**
   * PUT requests - queued (no throttling)
   * 
   * @param {string} url - The API endpoint URL
   * @param {Object} data - Data to send in the request body
   * @param {Object} config - Axios configuration object
   * @returns {Promise} - Queued PUT request
   * 
   * Same queuing as POST since it's also a write operation
   */
  static async put(url: string, data?: any, config?: AxiosRequestConfig) {
    // Log queue status before adding request
    logQueueStatus();
    
    return writeQueue.add(() => Api.put(url, data, config));
  }

  /**
   * DELETE requests - queued (no throttling)
   * 
   * @param {string} url - The API endpoint URL
   * @param {Object} config - Axios configuration object
   * @returns {Promise} - Queued DELETE request
   * 
   * Same queuing as POST since it's also a write operation
   */
  static async delete(url: string, config?: AxiosRequestConfig) {
    // Log queue status before adding request
    logQueueStatus();
    
    return writeQueue.add(() => Api.delete(url, config));
  }

  /**
   * PATCH requests - queued (no throttling)
   * 
   * @param {string} url - The API endpoint URL
   * @param {Object} data - Data to send in the request body
   * @param {Object} config - Axios configuration object
   * @returns {Promise} - Queued PATCH request
   * 
   * Same queuing as POST since it's also a write operation
   */
  static async patch(url: string, data?: any, config?: AxiosRequestConfig) {
    // Log queue status before adding request
    logQueueStatus();
    
    return writeQueue.add(() => Api.patch(url, data, config));
  }

  /**
   * Search requests - special handling for search operations
   * 
   * @param {string} url - The API endpoint URL
   * @param {Object} config - Axios configuration object
   * @returns {Promise} - Queued search request
   * 
   * How it works:
   * 1. Request is added to searchQueue (max 1 concurrent)
   * 2. All requests are processed in order (no dropping)
   * 3. Most restrictive queue to prevent search server overload
   */
  static async search(url: string, config?: AxiosRequestConfig) {
    // Log queue status before adding request
    logQueueStatus();
    
    return searchQueue.add(() => Api.get(url, config));
  }

  /**
   * QUEUE MANAGEMENT METHODS
   * 
   * These methods allow monitoring and controlling the queues
   */

  /**
   * Get current status of all queues
   * 
   * @returns {Object} - Status object with queue information
   * 
   * Returns:
   * - size: Number of requests waiting in queue
   * - pending: Number of requests currently being processed
   */
  static getQueueStatus(): AllQueueStatus {
    return {
      read: {
        size: readQueue.size,      // How many GET requests are waiting
        pending: readQueue.pending, // How many GET requests are running
      },
      write: {
        size: writeQueue.size,     // How many write requests are waiting
        pending: writeQueue.pending, // How many write requests are running
      },
      search: {
        size: searchQueue.size,    // How many search requests are waiting
        pending: searchQueue.pending, // How many search requests are running
      },
    };
  }

  /**
   * Clear all queues (use with caution)
   * 
   * Removes all pending requests from all queues
   * Only use in emergency situations or when you need to reset the system
   */
  static clearQueues(): void {
    readQueue.clear();
    writeQueue.clear();
    searchQueue.clear();
  }

  /**
   * Pause all queues
   * 
   * Stops processing new requests but keeps existing ones running
   * Useful for maintenance or debugging
   */
  static pauseQueues(): void {
    readQueue.pause();
    writeQueue.pause();
    searchQueue.pause();
  }

  /**
   * Resume all queues
   * 
   * Restarts processing of queued requests
   * Use after pauseQueues() to resume normal operation
   */
  static resumeQueues(): void {
    readQueue.start();
    writeQueue.start();
    searchQueue.start();
  }
}

export default ThrottledApi; 
